/**
 * L2 drill-down: every server in the joined inventory against every protection
 * source. The full list behind the L1 coverage donut (design rules §1) — this
 * is where a table belongs.
 *
 * The exclusion list is a first-class filter rather than a hidden subtraction:
 * "deliberately excluded" is a decision somebody made and can be reviewed, and
 * seeing it beside "backed up by nothing" is the point. A server that quietly
 * moved from one bucket to the other is exactly the kind of drift the annual
 * catalogue review used to catch a year late.
 */

import { Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { FilterRow } from "@/components/board";
import { type BackupCoverageRow, useBackupCoverage } from "@/lib/api";
import { formatCount, formatRelativeTime } from "@/lib/charts/config";
import { useDrillParams } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { PROTECTION_PARAM } from "./params";
import {
  COVERAGE_BUCKET_ORDER,
  COVERAGE_BUCKET_STYLE,
  type CoverageBucket,
  coverageStyle,
} from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Backup",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to="/backup" className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Coverage", current: true },
];

/**
 * Which donut bucket a row belongs to. `none` splits in two: a server that is
 * unprotected because nobody noticed, and one that is unprotected because
 * somebody decided. Filtering them together is what buried 14 real findings
 * under 45 deliberate ones.
 */
function bucketOf(server: BackupCoverageRow): CoverageBucket {
  if (server.state === "none" && server.exclusion_kind) {
    return "excluded";
  }
  return server.state;
}

export function BackupCoveragePage() {
  const coverage = useBackupCoverage();
  const drill = useDrillParams(PROTECTION_PARAM);
  const buckets = drill.getList(PROTECTION_PARAM);

  const data = coverage.data;
  const all = data?.servers ?? [];
  const rows = all.filter((server) => buckets.length === 0 || buckets.includes(bucketOf(server)));

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Backup coverage"
        subtitle="Every server against every protection source: is it backed up, by what, and when was it last proven?"
        actions={
          data?.collected_at ? <Freshness label="Collected" since={data.collected_at} /> : undefined
        }
      />

      {coverage.error && (
        <Alert tone="danger" title="Couldn't load coverage">
          {coverage.error instanceof Error
            ? coverage.error.message
            : "The NOVA API did not respond."}
        </Alert>
      )}

      {coverage.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!data.configured ? (
          <PanelCard title="Coverage" caption="Not available">
            <Typography variant="body-sm" tone="muted">
              {data.status_detail}
            </Typography>
          </PanelCard>
        ) : (
          <>
            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: PROTECTION_PARAM,
                  label: "Protection",
                  noun: "bucket",
                  options: COVERAGE_BUCKET_ORDER.map((bucket) => ({
                    value: bucket,
                    label: COVERAGE_BUCKET_STYLE[bucket].label,
                  })),
                },
              ]}
            />

            <PanelCard
              title="Servers"
              caption={
                rows.length === all.length
                  ? `${formatCount(all.length)} servers in the joined inventory`
                  : `${formatCount(rows.length)} of ${formatCount(all.length)} servers match`
              }
              padding={rows.length === 0 ? "md" : "none"}
            >
              {rows.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No servers match this filter"
                  description="Clear the protection filter above to see the whole inventory."
                />
              ) : (
                <DataGrid<BackupCoverageRow>
                  data={rows}
                  getRowId={(server) => server.resource_id ?? server.name}
                  density="compact"
                  aria-label="Every inventoried server and what protects it"
                  getRowSearchText={(server) =>
                    [
                      server.name,
                      server.kind,
                      server.veeam_object,
                      server.azure_item,
                      server.exclusion_kind,
                      ...server.backups,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  columns={[
                    {
                      id: "protection",
                      header: "Protection",
                      accessor: (server) => bucketOf(server),
                      sortable: true,
                      cell: (server) => {
                        const style = coverageStyle(bucketOf(server));
                        return (
                          <Badge tone={style.tone} variant="subtle" size="xs">
                            <span className="inline-flex items-center gap-1">
                              <Icon icon={style.icon} size="xs" />
                              {style.short}
                            </span>
                          </Badge>
                        );
                      },
                    },
                    {
                      id: "name",
                      header: "Server",
                      accessor: (server) => server.name,
                      sortable: true,
                    },
                    { id: "kind", header: "Seen in", accessor: (server) => server.kind },
                    {
                      id: "by",
                      header: "Backed up by",
                      accessor: (server) =>
                        [server.veeam_object, server.azure_item].filter(Boolean).join(" + "),
                      cell: (server) => {
                        const by = [server.veeam_object, server.azure_item].filter(Boolean);
                        return (
                          <span className="text-fg-muted text-sm">
                            {by.length > 0 ? by.join(" + ") : "Nothing"}
                          </span>
                        );
                      },
                    },
                    {
                      id: "group",
                      header: "Resource group",
                      accessor: (server) => server.resource_group ?? "—",
                    },
                    {
                      // Only ever filled for an excluded row, and that is the
                      // point: the reason travels with the row, so nobody has
                      // to trust that the exclusion was deliberate.
                      id: "why",
                      header: "Why unprotected",
                      accessor: (server) => server.exclusion_reason ?? "",
                      cell: (server) => (
                        <span className="text-fg-muted text-sm">
                          {server.exclusion_reason ?? "—"}
                        </span>
                      ),
                    },
                    {
                      id: "last",
                      header: "Last proven",
                      accessor: (server) => server.last_success ?? "",
                      sortable: true,
                      cell: (server) => (
                        <span className="text-fg-muted text-xs">
                          {server.last_success ? formatRelativeTime(server.last_success) : "Never"}
                        </span>
                      ),
                    },
                  ]}
                />
              )}
              {data.errors.length > 0 && (
                <Typography variant="body-sm" tone="muted">
                  Collection problems: {data.errors.join(" · ")}
                </Typography>
              )}
            </PanelCard>
          </>
        ))
      )}
    </main>
  );
}
